import { keepPreviousData, useMutation, useQuery } from '@tanstack/react-query';
import { keys } from './keys';
import type { QueryOptions } from './query-client';
import { request } from './client';
import type {
  AdminContext,
  AdminProfile,
  ListQuery,
  Paginated,
  Tenant,
  TenantStatus,
} from './types';

/** /admin/auth/*: staff console context (StaffGuard: tenant tokens get 403). */
export const adminApi = {
  check: () => request<{ isStaff: boolean; userId: string }>('/admin/auth/check'),
  profile: (tenantId?: string) =>
    request<AdminProfile>('/admin/auth/profile', { query: { tenantId } }),
  tenants: (query: ListQuery & { status?: TenantStatus; accountManagerId?: string } = {}) =>
    request<Paginated<Tenant>>('/admin/auth/tenants', { query }),
  switchTenant: (tenantId: string) =>
    request<AdminContext>('/admin/auth/switch-tenant', { method: 'POST', body: { tenantId } }),
};

type TenantsQuery = NonNullable<Parameters<typeof adminApi.tenants>[0]>;

/** Console context for the signed-in staff member, optionally pinned to a tenant. */
export function useAdminProfile(tenantId?: string, options: QueryOptions = {}) {
  return useQuery({
    queryKey: keys.admin.profile(tenantId),
    queryFn: () => adminApi.profile(tenantId),
    ...options,
  });
}

/**
 * Every tenant (staff). The dashboard, plans, domains and WhatsApp pages share
 * it: one request per distinct query, served from cache on the next page.
 */
export function useTenants(query: TenantsQuery = {}, options: QueryOptions = {}) {
  return useQuery({
    queryKey: keys.tenants.adminList(query),
    queryFn: () => adminApi.tenants(query),
    placeholderData: keepPreviousData,
    ...options,
  });
}

/** Validates a console tenant selection. The context it returns is page state; nothing cached changes. */
export function useSwitchTenant() {
  return useMutation({ mutationFn: (tenantId: string) => adminApi.switchTenant(tenantId) });
}
